import React from 'react';
import { useBooking } from '../../contexts/booking/BookingContext';
import { isBefore, startOfDay, isSameDay } from 'date-fns';
import { format } from 'date-fns';
import { useDateTimeSelection, TimeSlotDisplay } from '../../hooks/useDateTimeSelection';
import { getTotalDuration } from '../../utils/bookingUtils';

const DateTimeSelection: React.FC = () => {
  const { bookingState, nextStep, prevStep } = useBooking();
  const { selectedServices, selectedStaff, selectedDate: currentSelectedDate, selectedTime: selectedTimeISO } = bookingState;
  
  const {
    availableDates,
    timeSlotsForDisplay,
    isLoading,
    error,
    selectedTimeFormatted,
    handleDateSelection,
    handleTimeSelection,
    formatTimeForDisplay,
    currentSelectedDate: hookSelectedDate
  } = useDateTimeSelection();
  
  // Handle "Continue" button click
  const handleContinue = () => {
    if (currentSelectedDate && selectedTimeISO) {
      nextStep();
    }
  };

  return (
    <div>
      <h2 className="text-2xl font-serif text-[#232347] mb-2">Select Date & Time</h2>
      <p className="text-gray-600 mb-6">Choose when you'd like to schedule your appointment</p>
      
      {/* Selected service and staff reminder */}
      <div className="mb-6 bg-[#F1F7F4] p-4 rounded-md">
        <h3 className="text-lg font-medium text-[#232347] mb-2">Your Booking Details:</h3>
        <div className="space-y-2">
          <div>
            <h4 className="font-medium">Services:</h4>
            <ul className="ml-4 list-disc">
              {selectedServices.map(service => (
                <li key={service.id}>{service.name} ({service.duration} min)</li>
              ))}
            </ul>
          </div>
          <div>
            <h4 className="font-medium">Staff:</h4>
            <p>{selectedStaff ? selectedStaff.name : "No preference"}</p>
          </div>
          <div>
            <h4 className="font-medium">Total Duration:</h4>
            <p>{getTotalDuration(selectedServices)} min</p>
          </div>
        </div>
      </div>
      
      {/* Date selection - Organized by week */}
      <div className="mb-6">
        <h3 className="text-lg font-medium text-[#232347] mb-3">Select Date</h3>
        
        {/* Month indicator */}
        <div className="flex justify-between items-center mb-2">
          {availableDates.length > 0 && (
            <h4 className="font-medium text-gray-500">
              {format(availableDates[0], 'MMMM yyyy')}
            </h4>
          )}
          {availableDates.length > 7 && 
            availableDates[0] && 
            availableDates[availableDates.length - 1] && 
            format(availableDates[0], 'MMM') !== format(availableDates[availableDates.length - 1], 'MMM') && (
              <h4 className="font-medium text-gray-500">
                {format(availableDates[availableDates.length - 1], 'MMMM yyyy')}
              </h4>
          )}
        </div>
        
        {/* Week-by-week calendar view */}
        <div className="flex overflow-x-auto space-x-2 pb-2 scrollbar-thin scrollbar-thumb-[#B2D8C5] scrollbar-track-gray-100">
          {availableDates.map((date) => {
            const isSelected = currentSelectedDate ? isSameDay(date, currentSelectedDate) : false;
            const isPast = isBefore(startOfDay(date), startOfDay(new Date())) && !isSameDay(date, startOfDay(new Date()));

            return (
              <button
                key={date.toISOString()}
                onClick={() => !isPast && handleDateSelection(date)}
                disabled={isPast}
                className={`p-3 min-w-[70px] rounded-lg border text-center transition-colors focus:outline-none focus:ring-2 focus:ring-opacity-50 ${
                  isSelected
                    ? 'bg-[#B2D8C5] text-white border-[#B2D8C5] focus:ring-[#B2D8C5]'
                    : isPast 
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed border-gray-200 focus:ring-gray-300'
                      : 'bg-white hover:border-[#B2D8C5] border-gray-200 focus:ring-[#B2D8C5]'
                }`}
              >
                <div className={`font-medium ${isSelected ? 'text-white' : isPast ? 'text-gray-400' : 'text-[#232347]'}`}>
                  {format(date, 'EEE')}
                </div>
                <div className={`text-2xl font-bold my-1 ${isSelected ? 'text-white' : isPast ? 'text-gray-400' : 'text-[#232347]'}`}>
                  {format(date, 'd')}
                </div>
                <div className={`text-xs ${isSelected ? 'text-white' : isPast ? 'text-gray-400' : 'text-gray-500'}`}>
                  {format(date, 'MMM')}
                </div>
              </button>
            );
          })}
        </div>
      </div>
      
      {/* Time selection */}
      <div>
        <h3 className="text-lg font-medium text-[#232347] mb-3">Select Time</h3>
        
        {isLoading ? (
          <div className="flex justify-center items-center p-8">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#B2D8C5]"></div>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
              {timeSlotsForDisplay.map(slot => {
                const isSelected = selectedTimeFormatted === slot.time;
                
                return (
                  <button 
                    key={slot.time} 
                    className={`border rounded-lg p-2 text-center transition duration-150 ${
                      !slot.available 
                        ? 'border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed opacity-60'
                        : isSelected
                          ? 'border-[#B2D8C5] bg-[#F1F7F4] text-[#232347] font-medium cursor-pointer'
                          : 'border-gray-200 hover:border-[#B2D8C5] hover:bg-[#F1F7F4]/30 text-gray-700 cursor-pointer'
                    }`}
                    onClick={() => handleTimeSelection(slot.time)}
                    disabled={!slot.available}
                    aria-label={`Select time: ${formatTimeForDisplay(slot.time)}`}
                    aria-selected={isSelected}
                    aria-disabled={!slot.available}
                  >
                    <p className="text-sm">
                      {formatTimeForDisplay(slot.time)}
                    </p>
                  </button>
                );
              })}
            </div>
            
            {selectedTimeFormatted && (
              <div className="mt-4 p-2 text-center text-[#232347] bg-[#F1F7F4] rounded-md">
                <p>Selected time: <span className="font-medium">{formatTimeForDisplay(selectedTimeFormatted)}</span></p>
              </div>
            )}
            
            {error && (
              <div className="mt-4 p-3 bg-red-50 text-red-700 rounded-md">
                <p>{error}</p>
              </div>
            )}
            
            {/* Inform user if no slots are available after a successful fetch */}
            {!isLoading && !error && timeSlotsForDisplay.every(slot => !slot.available) && currentSelectedDate && selectedStaff && selectedServices.length > 0 && (
                 <div className="mt-4 text-sm text-gray-500 text-center">
                    <p>No slots available for the selected criteria. Try changing the date, staff, or services.</p>
                </div>
            )}
          </>
        )}
      </div>
      
      {/* Navigation buttons */}
      <div className="mt-8 flex justify-between">
        <button
          onClick={prevStep}
          className="px-6 py-2 rounded-md border border-[#B2D8C5] text-[#232347] hover:bg-[#F1F7F4] transition-colors"
        >
          Back
        </button>
        <button
          onClick={handleContinue}
          disabled={!currentSelectedDate || !selectedTimeISO || isLoading}
          className="px-6 py-2 rounded-md text-white bg-[#B2D8C5] hover:bg-[#88bfa3] transition-colors disabled:bg-gray-300"
        >
          {isLoading ? 'Loading...' : 'Continue'}
        </button>
      </div>
      
      {/* Legend for time slot status */}
      <div className="mt-4 flex items-center justify-center space-x-6 text-sm text-gray-500">
        <div className="flex items-center">
          <div className="w-4 h-4 bg-gray-100 border border-gray-200 rounded mr-2"></div>
          <span>Unavailable</span>
        </div>
        <div className="flex items-center">
          <div className="w-4 h-4 border border-gray-200 hover:border-[#B2D8C5] rounded mr-2"></div>
          <span>Available</span>
        </div>
        <div className="flex items-center">
          <div className="w-4 h-4 bg-[#F1F7F4] border border-[#B2D8C5] rounded mr-2"></div>
          <span>Selected</span>
        </div>
      </div>
    </div>
  );
};

export default DateTimeSelection;